<template>
  <div class="associate-info">
    <div class="associate-photo" @click.stop="openAssociateProfile">
      <user-avatar
        :has-modal="!isOpenProfile"
        :width="avatarSize"
        :height="avatarSize"
        :image-path="photoUrl ? photoUrl : undefined"
      />
    </div>
    <div class="associate-name">
      <div class="associate-name__identity">
        <p :style="{ fontSize: '16px', cursor: 'pointer' }" @click.stop="openNameAssociateProfile">
          {{ titledFirstName }} {{ titledLastName }}
        </p>
      </div>

      <div class="associate-name__meta flex align-items-center">
        <p :style="{ textWrap: 'nowrap', overflow: 'visible' }" v-if="associateNumber">
          {{ associateNumber }}
        </p>
        <template v-if="role">
          <a-divider type="vertical" />
          <p>{{ role }}</p></template
        >
      </div>

      <p v-if="advancedPaymentInfo" class="associate-name__payment-info">{{ advancedPaymentInfo }}</p>
      <p v-if="legalEntityName" class="associate-name__legal-entity">{{ legalEntityName }}</p>
      <p v-if="scheduledType">{{ formattedScheduledType }}</p>

      <div v-if="hasAssociateBadges" class="associate-name__badges">
        <template v-if="unverified !== undefined && unverified !== null">
          <a-tooltip v-if="showCompletionStatus" :title="completionStatusTooltip">
            <div class="associate-badge completion-status" :class="completionStatusClass">
              {{ completionStatusLabel }}
            </div>
          </a-tooltip>
          <a-tooltip v-else-if="unverified" title="Completion Status: Incomplete">
            <div class="unverified">Incomplete</div>
          </a-tooltip>
        </template>
        <a-tooltip v-if="showECertStatus" :title="eCertStatusTooltip">
          <div class="associate-badge ecert-status" :class="eCertStatusClass">
            {{ eCertStatusLabel }}
          </div>
        </a-tooltip>
      </div>
    </div>
  </div>
</template>

<script>
import UserAvatar from '@/components/ui/user-avatar.vue'
import permissionMixin from '@/mixins/permissionMixin'
import { AGENCY_TYPES } from '@/store/modules/legal-entity/constants'
import { SCHEDULED_TYPE } from '@/store/modules/profile/constants'
import Util from '@/util'
import { Divider, Tooltip } from 'ant-design-vue'
import { createNamespacedHelpers } from 'vuex'

const { mapActions: modalActions } = createNamespacedHelpers('modals')

export default {
  components: {
    'a-divider': Divider,
    'a-tooltip': Tooltip,
    'user-avatar': UserAvatar
  },
  props: {
    firstName: {
      type: String,
      required: true
    },
    lastName: {
      type: String,
      required: true
    },
    photoUrl: {
      type: String,
      required: false
    },
    role: {
      type: String,
      required: false
    },
    unverified: {
      type: Boolean,
      required: false,
      default: undefined
    },
    associateNumber: {
      type: String,
      required: false,
      default: ''
    },
    paymentInfo: {
      type: Object,
      default: undefined
    },
    avatarSize: {
      type: Number,
      default: 40
    },
    scheduledType: {
      type: String,
      default: undefined
    },
    showCompletionStatus: {
      type: Boolean,
      default: false
    },
    epeopleCertified: {
      type: Boolean,
      default: undefined
    },
    showECertStatus: {
      type: Boolean,
      default: false
    },
    handleOpenProfile: {
      type: Function,
      default: undefined
    },
    isOpenProfile: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      AGENCY_TYPES
    }
  },
  mixins: [permissionMixin],
  computed: {
    titledFirstName() {
      return Util.titleCase(this.firstName)
    },
    titledLastName() {
      return Util.titleCase(this.lastName)
    },
    completionStatusLabel() {
      return this.unverified ? 'Incomplete' : 'Complete'
    },
    completionStatusTooltip() {
      return `Completion Status: ${this.completionStatusLabel}`
    },
    completionStatusClass() {
      return this.unverified ? 'completion-status--incomplete' : 'completion-status--complete'
    },
    eCertStatusLabel() {
      return this.epeopleCertified ? 'eCert' : 'No eCert'
    },
    eCertStatusTooltip() {
      if (this.epeopleCertified) {
        return 'ePeople Certified: premium verification is active for this associate.'
      }

      return 'Not ePeople Certified. Complete eCert requirements to receive the premium badge.'
    },
    eCertStatusClass() {
      return this.epeopleCertified ? 'ecert-status--certified' : 'ecert-status--uncertified'
    },
    hasAssociateBadges() {
      return this.showECertStatus || (this.unverified !== undefined && this.unverified !== null)
    },
    advancedPaymentInfo() {
      const paymentType = this.paymentInfo?.payment_type

      if (!paymentType) {
        return ''
      }

      const normalizedPaymentType = String(paymentType).toLowerCase()

      if (normalizedPaymentType === 'contractor' && AGENCY_TYPES[this.paymentInfo.legal_entity?.type]) {
        return `${AGENCY_TYPES[this.paymentInfo.legal_entity?.type]} ${
          this.paymentInfo.legal_entity?.type === 'agency' || this.paymentInfo.legal_entity?.type === 'marketplace'
            ? 'Contractor'
            : ''
        }`
      } else if (normalizedPaymentType === 'employee') {
        return `${Util.capitalize(this.paymentInfo.method ?? '')} Employee`
      } else {
        return `${Util.capitalize(String(paymentType))}`
      }
    },
    legalEntityName() {
      return this.paymentInfo?.legal_entity?.name || ''
    },
    formattedScheduledType() {
      return SCHEDULED_TYPE[this.scheduledType]
    }
  },
  methods: {
    ...modalActions(['openModal']),

    openAssociateProfile() {
      if (this.handleOpenProfile) {
        this.handleOpenProfile()
      } else if (this.associateNumber && this.isOpenProfile && this.hasPermission('manage_associates')) {
        this.openModal({
          type: 'profile',
          data: {
            visible: true,
            associate_number: this.associateNumber,
            edit: () => {},
            open_tab: 'personal'
          }
        })
      }
    },

    openNameAssociateProfile() {
      if (this.associateNumber && this.hasPermission('manage_associates')) {
        this.openModal({
          type: 'profile',
          data: {
            visible: true,
            associate_number: this.associateNumber,
            edit: () => {},
            open_tab: 'personal'
          }
        })
      }
    }
  }
}
</script>

<style scoped>
.associate-info {
  display: flex;
  flex-direction: row;
  flex-wrap: nowrap;
  align-items: flex-start;
  gap: 12px;
  max-width: 250px;
}

.associate-photo {
  cursor: pointer;
}

.associate-name {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  text-align: left;
  gap: 3px;
  width: 100%;
  min-width: 0;
}

.associate-name p,
.associate-name .unverified,
.associate-name .associate-badge {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  margin: 0;
}

.associate-name > * {
  min-width: 0;
  overflow: hidden;
  width: 100%;
}

.associate-name__identity {
  width: 100%;
}

.associate-name__payment-info,
.associate-name__legal-entity {
  font-weight: 500;
}

.associate-name .associate-name__legal-entity {
  white-space: normal;
  overflow: visible;
  text-overflow: clip;
  overflow-wrap: anywhere;
}

.associate-name__meta {
  color: var(--text-color-secondary);
  font-size: 13px;
  line-height: 18px;
}

.associate-name__badges {
  display: inline-flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 5px;
  width: 100%;
  padding-top: 2px;
}

.unverified {
  color: #ef4444;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.associate-badge {
  flex: 0 0 auto;
  max-width: 104px;
  min-height: 18px;
  border: 1px solid transparent;
  padding: 1px 6px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 600;
  line-height: 16px;
  letter-spacing: 0;
}

.completion-status--complete {
  color: #8fe7bf;
  border-color: rgba(16, 185, 129, 0.32);
  background: rgba(16, 185, 129, 0.12);
}

.completion-status--incomplete {
  color: #f5a6a6;
  border-color: rgba(248, 113, 113, 0.34);
  background: rgba(239, 68, 68, 0.12);
}

.ecert-status--certified {
  color: #f7cf72;
  border-color: rgba(245, 158, 11, 0.44);
  background: rgba(245, 158, 11, 0.13);
}

.ecert-status--uncertified {
  color: #a8b0bd;
  border-color: rgba(156, 163, 175, 0.28);
  background: rgba(156, 163, 175, 0.1);
}

::v-deep .ant-divider {
  background: var(--text-color-secondary) !important;
}
</style>
